/*
 * Manages and creates a dynamic text box for a selected piece of text
 * Includes a submit and cancel button
 * Only allows one dynamic text box to be open at a time
 * When closed, will revert back to text
 */

define(["jquery", "messaging/messenger", "underscore", "rangyinputs"], function (
  $,
  Messenger,
  _ /*, rangyinputs*/,
) {
  function TextBox() {
    this.textBox = null;
    this.isCurrentTextBox = function (selector) {
      //check if it's the current text box by seeing if theres an input or textarea in the selector
      return selector.find("input").length !== 0 || selector.find("textarea").length !== 0;
    };
    this.isTextBoxOpen = function () {
      return this.textBox !== null;
    };
    /*
     * @selector: the jQuery selector where we insert the textbox.
     * @model: the Backbone model containing the attribute we are editing
     * @attribute: the attribute in the Backbone model we are editing
     * @textarea: optional boolean parameter (defaults to false if missing).  If true, we show a textarea instead of an input field.
     * @placeholder: i18n placeholder text key to use in the textbox when empty or in the textbox area when empty (so that it's not just a blank area on the screen)
     */
    this.setInitialText = function (
      selector,
      model,
      attribute,
      textarea,
      placeholder,
      doNotModifyPlaceholder,
      isPassword,
    ) {
      //set the classes on the selector element
      selector.addClass("text-box-field closed");
      if (textarea) {
        selector.addClass("text-area-field");
      }
      model.onSynced(function () {
        var textValue = model.get(attribute);
        //show a placeholder if the text value of the model's attribute is empty, we have a placeholder to show, and the model isn't being requested still
        if (_.isEmpty(textValue) && placeholder) {
          this._createTextPlaceholder(selector, placeholder, doNotModifyPlaceholder);
        } else {
          if (isPassword) {
            var newTextValue = "";
            for (var i = 0; i < textValue.length; i++) {
              newTextValue = newTextValue + "&bull;";
            }
            selector.html(newTextValue);
          } else {
            textValue = _.escape(textValue);
            selector.html(textValue);
          }
        }
      }, this);
    };
    /*
     * @selector: the jQuery selector where we insert the textbox.
     * @model: the Backbone model containing the attribute we are editing
     * @attribute: the attribute in the Backbone model we are editing
     * @textarea: optional boolean parameter (defaults to false if missing).  If true, we show a textarea instead of an input field.
     * @placeholder: i18n placeholder text key to use in the textbox when empty or in the textbox area when empty (so that it's not just a blank area on the screen)
     */
    this.create = function (
      selector,
      model,
      attribute,
      textarea,
      placeholder,
      doNotModifyPlaceholder,
      isPassword,
      typeahead,
      doNotAllowEmpty,
      emptyTextboxMessage,
    ) {
      if (this.isTextBoxOpen()) {
        return; //if a textbox is open, then that means the previous one has an error which caused it not to be closed, so
        //don't allow a new one to be opened
      }
      this._createTextBox(
        selector,
        model,
        attribute,
        textarea,
        placeholder,
        doNotModifyPlaceholder,
        isPassword,
        typeahead,
        doNotAllowEmpty,
        emptyTextboxMessage,
      );
    };
    this._createTextBox = function (
      selector,
      model,
      attribute,
      textarea,
      placeholder,
      doNotModifyPlaceholder,
      isPassword,
      typeahead,
      doNotAllowEmpty,
      emptyTextboxMessage,
    ) {
      //translate placeholder or set it to empty string
      var placeholderText;
      if (!placeholder) {
        placeholder = "";
        placeholderText = "";
      } else if (typeof placeholder === "function") {
        placeholderText = placeholder({ type: "textbox" });
      } else {
        placeholderText = $.t(placeholder);
      }

      var height = selector.height();
      var lineHeight = parseInt(selector.css("line-height"), 10);
      var textboxAsTextarea = false;
      if (height - 10 > lineHeight || textarea) {
        textboxAsTextarea = true; //show what would normally be a textbox as a text area because the text takes up more than one line
      }
      if (!textboxAsTextarea) {
        if (isPassword) {
          this.isPassword = true; //store it so we don't show the password when leaving the field
          selector.html(
            '<div class="input-append"><input id="textBoxField" class="text-box" type="password" value="' +
              model.get(attribute) +
              '"><button class="btn input-btn astute-blue" id="textBoxCancel" type="button"><i class="icon-undo icon-white"></i></button></div>',
          );
        } else if (doNotAllowEmpty) {
          selector.html(
            '<div class="input-append"><input id="textBoxField" class="text-box" type="text" value="' +
              _.escape(model.get(attribute)) +
              '"><button class="btn input-btn astute-blue" id="textBoxCancel" type="button"><i class="icon-undo icon-white"></i></button></div>' +
              '<div id="emptyError" class="error-message-small">' +
              $.t(emptyTextboxMessage) +
              "</div>",
          );
        } else {
          selector.html(
            '<div class="input-append"><input id="textBoxField" class="text-box" type="text" value="' +
              _.escape(model.get(attribute)) +
              '"><button class="btn input-btn astute-blue" id="textBoxCancel" type="button"><i class="icon-undo icon-white"></i></button></div>',
          );
        }
        selector.find("input").css("line-height", lineHeight + "px"); //needed for IE not to cut off part of the text
        selector.find("input").height(lineHeight); //need to define the height of the input explicitly
      } else {
        //textarea version
        selector.html(
          '<div class="input-append"><textarea id="textBoxField" class="text-box">' +
            _.escape(model.get(attribute)) +
            '</textarea><button class="btn input-btn astute-blue" id="textBoxCancel" type="button"><i class="icon-undo icon-white"></i></button></div>',
        );
        selector.find("textarea").css("height", height + "px");
        selector.find("textarea").css("line-height", lineHeight + "px"); //needed for IE not to cut off part of the text
      }

      var textBoxField = selector.find("#textBoxField");
      textBoxField.attr("placeholder", placeholderText).trigger("focus");
      var endSel = textBoxField.val().length;
      $(textBoxField).setSelection(endSel, endSel);

      selector.find("#emptyError").hide();

      //disable the on hover effects
      selector.removeClass("closed");

      //prevent propagation of hidden popover event (will mess up modals)
      selector.find("#textBoxCancel").on("hidden", function (event) {
        event.stopPropagation();
      });

      //save textbox parameters
      this.textBox = {
        selector: selector,
        model: model,
        attribute: attribute,
        previousValue: model.get(attribute),
        placeholder: placeholder,
        isPassword: isPassword,
        textarea: textarea,
        doNotModifyPlaceholder: doNotModifyPlaceholder,
      };

      var _this = this;
      if (typeahead) {
        var originalUpdater = typeahead.updater;
        typeahead.updater = function (item) {
          console.log("chose " + item);
          var result = item;
          if (originalUpdater) {
            result = originalUpdater(item);
            textBoxField.val(result);
          } else {
            textBoxField.val(item);
          }
          _this.enter(selector, model, attribute, _this, isPassword, textarea, textBoxField);
          return result;
        };
        textBoxField.typeahead(typeahead);
        textBoxField.trigger("focus");
      }
      textBoxField.on("keydown", function (event) {
        if (event.which == 13 || event.key == "Enter") {
          //enter
          if (!textarea && !typeahead) {
            _this.enter(selector, model, attribute, _this, isPassword, textarea);
            return false;
          }
        } else if (event.which == 27 || event.key == "Escape") {
          //escape
          _this.cancel(selector, model, attribute, _this, isPassword);
          return false;
        }
      });
      textBoxField.on("keyup", function () {
        if (doNotAllowEmpty) {
          if (textBoxField.val() === "") {
            selector.find("#emptyError").show();
          } else {
            selector.find("#emptyError").hide();
          }
        }
      });
      //cancel:  We need a fancy set up to capture the click on the cancel button before the blur event occurs
      //mousedown fires before blur, so then inside the callback we need to check if the mouse up event occurs.
      selector.find("#textBoxCancel").on("mousedown", function () {
        selector.find("#textBoxCancel").off("mouseup"); //cancel any old mouse up event handlers that didn't fire
        selector.find("#textBoxCancel").one("mouseup", function () {
          _this.cancel(selector, model, attribute, _this, isPassword);
        });
        return false;
      });

      //save when we lose focus
      textBoxField.on("blur", function () {
        if (!typeahead || textBoxField.val() === "") {
          var success = _this.enter(selector, model, attribute, _this, isPassword, textarea);
          if (!success && !Messenger.reqres.request("dialog:window:open")) {
            //if the enter event wasn't successful (b/c the text was invalid), then refocus on the textbox...unless a dialog window is open
            //because the window would fight for focus, especially in IE
            textBoxField.trigger("focus"); //if false, keeps focus on the textbox
          }
        } else if (!selector.find("ul:visible").length) {
          _this.cancel(selector, model, attribute, _this, isPassword);
        }
      });
    };
    this._createTextPlaceholder = function (selector, placeholder, doNotModifyPlaceholder) {
      var placeholderText;
      if (typeof placeholder === "function") {
        placeholderText = placeholder({ type: "span" });
      } else {
        placeholderText = $.t(placeholder);
        if (!doNotModifyPlaceholder) {
          placeholderText = $.t("textbox.placeholder", { name: placeholderText });
        }
      }
      selector.html("<span class='textbox-placeholder'>" + placeholderText + "</span>");
    };
    this.enter = function (selector, model, attribute, _this, isPassword, textarea, textBoxField) {
      var textBoxCancel = selector.find("#textBoxCancel");
      textBoxCancel.popover("destroy"); //destroy any existing popover
      if (!textBoxField) {
        textBoxField = selector.find("#textBoxField");
      }
      //save changes
      var value = textBoxField.val();
      if (!value) {
        value = "";
      } else if (!textarea) {
        value = value.trimStrict(); //trim whitespace
      }

      var error = model.validateAttribute(attribute, value);
      if (error) {
        var message = "<h5>" + error + "</h5>";
        textBoxCancel.popover({
          placement: "top",
          html: "true",
          content: message,
          trigger: "manual",
          container: ".input-append",
        });
        //trigger popover
        textBoxCancel.popover("show");
      } else {
        //if a change event fires and causes a render, it will disrupt focusing from one textbox to another.
        //As a result, we have to hack a bit and silence the change event, and then manually change the dirty flag
        if (model.get(attribute) !== value) {
          model.set(attribute, value, { silent: true });
          model.dirty = true;
          //trigger "enter" event
          console.log("enter event");
          model.trigger("enter");
          //trigger enter:attribute event
          model.trigger("enter:" + attribute);
        }

        //close text box (leave optional placeholder if text is empty)
        if (_.isEmpty(value) && _this.textBox.placeholder) {
          _this._createTextPlaceholder(
            selector,
            _this.textBox.placeholder,
            _this.textBox.doNotModifyPlaceholder,
          );
        } else {
          if (isPassword) {
            var newTextValue = "";
            for (var i = 0; i < value.length; i++) {
              newTextValue = newTextValue + "&bull;";
            }
            selector.html(newTextValue);
          } else {
            value = _.escape(value);
            selector.html(value);
          }
        }
        _this.finishUp(selector, _this);
      }
      return !error;
    };
    this.cancel = function (selector, model, attribute, _this, isPassword) {
      //close text box (leave placeholder if text is empty)
      if (_.isEmpty(model.get(attribute)) && _this.textBox.placeholder) {
        _this._createTextPlaceholder(
          selector,
          _this.textBox.placeholder,
          _this.textBox.doNotModifyPlaceholder,
        );
      } else {
        if (!isPassword) {
          selector.html(_.escape(model.get(attribute)));
        } else {
          var newTextValue = "";
          for (var i = 0; i < model.get(attribute).length; i++) {
            newTextValue = newTextValue + "&bull;";
          }
          selector.html(newTextValue);
        }
      }
      //trigger "cancel" event on model
      model.trigger("cancel");
      _this.finishUp(selector, _this);
    };
    this.finishUp = function (selector, _this) {
      _this.textBox = null; //reset textbox parameters after enter or cancel event
      selector.addClass("closed"); //reapply hover effect
    };
    this.closeTextBox = function () {
      if (this.isTextBoxOpen()) {
        //if the text box is open, try saving the context and return whether it succeeded or not
        return this.enter(
          this.textBox.selector,
          this.textBox.model,
          this.textBox.attribute,
          this,
          this.textBox.isPassword,
          this.textBox.textarea,
        );
      } else {
        return true;
      }
    };
  }
  var textbox = new TextBox();

  Messenger.vent.on(
    "cancel:textbox",
    function () {
      if (this.isTextBoxOpen()) {
        this.cancel(this.textBox.selector, this.textBox.model, this.textBox.attribute, this);
      }
    },
    textbox,
  ); //cancel any open textboxes

  //Change the contents of the textbox to the newString variable, but doesn't save the change
  Messenger.vent.on(
    "edit:textbox",
    function (newString) {
      if (this.isTextBoxOpen()) {
        this.textbox.selector.find("#textBoxField").val(newString);
      }
    },
    textbox,
  );

  //Use reqres to return whether the request was able to properly save the textbox contents or an error occured
  Messenger.reqres.setHandler(
    "save:textbox",
    function () {
      return this.closeTextBox();
    },
    textbox,
  );

  return textbox;
});
